import React, { useCallback, useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { useMarket } from "./MarketContext"
import { trackLeadConversion } from "./leadConversion"
import { openSignUpModal } from "./signupEmbedUrl"
import ProviderInterestForm from "./ProviderInterestForm"

export { openSignUpModal }

function currentPageRoute() {
  if (typeof window === "undefined") return "/"
  return window.location.pathname || "/"
}

function consumeSignupQueryParam() {
  const currentUrl = new URL(window.location.href)
  if (currentUrl.searchParams.get("signup") !== "1") return false

  currentUrl.searchParams.delete("signup")
  window.history.replaceState(
    window.history.state,
    "",
    `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`
  )
  return true
}

/** Single modal host — mount once (e.g. in Layout). */
export function SignUpModalHost() {
  const { market } = useMarket() || { market: "gb" }
  const [open, setOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [formKey, setFormKey] = useState(0)

  const close = useCallback(() => setOpen(false), [])
  const openModal = useCallback(() => {
    setFormKey((key) => key + 1)
    setOpen(true)
  }, [])

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    const onOpen = () => openModal()
    window.addEventListener("collar-open-signup-modal", onOpen)
    if (consumeSignupQueryParam()) openModal()
    return () => window.removeEventListener("collar-open-signup-modal", onOpen)
  }, [openModal])

  useEffect(() => {
    if (!open) return undefined

    const onKeyDown = (event) => {
      if (event.key === "Escape") close()
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    window.addEventListener("keydown", onKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener("keydown", onKeyDown)
    }
  }, [open, close])

  const onSubmitted = useCallback(() => {
    trackLeadConversion({
      dataLayerEvent: "provider_interest_submitted",
      method: "provider-interest",
    })
  }, [])

  if (!open || !mounted) return null

  return createPortal(
    <div className="signup-modal-backdrop" role="presentation" onClick={close}>
      <div
        className="signup-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Sign Up"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="signup-modal__close"
          aria-label="Close"
          onClick={close}
        >
          ×
        </button>
        <div className="signup-modal__body">
          <ProviderInterestForm
            key={formKey}
            market={market || "gb"}
            pageRoute={currentPageRoute()}
            onSubmitted={onSubmitted}
          />
        </div>
      </div>
    </div>,
    document.body
  )
}

export default function SignUpLink({
  triggerClass = "login_button",
  buttonText = "Sign Up",
  wrapperClassName = null,
}) {
  const trigger = (
    <button
      type="button"
      className={`${triggerClass} signup-trigger`}
      onClick={openSignUpModal}
    >
      {buttonText}
    </button>
  )

  return wrapperClassName ? <div className={wrapperClassName}>{trigger}</div> : trigger
}
